import React, { useState, useEffect, useRef, useCallback } from "react";
import { StyleSheet, View, SafeAreaView, useWindowDimensions, FlatList, ActivityIndicator } from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomText from '../../../components/CustomText/CustomText';
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { useTranslation } from "react-i18next";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import { changeDateFromYYYYMMDDToDDMMYYYY, formatDateForUI } from "../../../constants/FormatDate";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { useTableHelper } from "../../../hooks/useTableHelper";
import Swal from "sweetalert2";
import { showMessage } from "react-native-flash-message";
import * as ExpoDocumentPicker from "expo-document-picker";
import {
    Menu,
    MenuOptions,
    MenuOption,
    MenuTrigger,
    renderers,
} from 'react-native-popup-menu';

const AdditionalQualificationsListView = React.memo(
    ({
        item,
        index,
        handleEditPress,
        styles,
        additionalQualHelper,
        t,
        handleDelete,
        resetDates,
        openCalendar,
    }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [showCalendarDoa, setShowCalendarDoa] = useState(false);
        const [attachments, setAttachments] = useState({});
        const attachmentsRef = useRef(null);
        const submitClickedRef = useRef(null);
        const [selectedData, setSelectedData] = useState({
            startDate: item.date || "",
            endDate: "",
        });

        const toggleExpand = () => {
            setIsExpanded(!isExpanded);
        };

        useEffect(() => {
            setAttachments(item.attachments || {});
        }, [item.attachments]);

        useEffect(() => {
            if (resetDates) {
                const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
                setSelectedData({
                    startDate: item.date || today,
                });
                const from = changeDateFromYYYYMMDDToDDMMYYYY(item.date || today);
            }
        }, [resetDates, item.date]);

        const CreateDivider = () => {
            return (
                <View
                    style={{
                        borderBottomColor: '#000000',
                        borderBottomWidth: StyleSheet.hairlineWidth,
                    }}
                />
            );
        };

        const removeAttachment = useCallback((index, item) => {
            handleEditPress(index, item, "fileName", '');
            handleEditPress(index, item, "fileType", '');
            handleEditPress(index, item, "fileData", null);
        }, [handleEditPress, index, item]);


        const showAttachments = (index, item) => {
            //* the attachments section to be shown/hidden
            return (
                <View>
                    <View style={styles.attachmentContainer}>
                        <View style={styles.attachmentIcons}>
                            <Ionicons
                                name="document-outline"
                                style={styles.icons}
                            />
                        </View>
                        <View style={styles.attachmentNameContainer}>
                            <CustomText style={styles.attachmentNameText}>{item.fileName}</CustomText>
                        </View>
                        <View style={styles.attachmentIcons}>
                            <Menu
                                renderer={renderers.SlideInMenu}
                                style={styles.attachmentMenu}
                            >
                                <MenuTrigger>
                                    <Ionicons
                                        name="ellipsis-vertical"
                                        style={styles.dotsIcon}
                                    />
                                </MenuTrigger>
                                <MenuOptions customStyles={optionsStyles}>
                                    <MenuOption onSelect={() => openAttachment(index, item)} customStyles={cancelOptionStyles} text={t('common:open')}>
                                    </MenuOption>
                                    {CreateDivider()}
                                    <MenuOption onSelect={() => downloadAttachment(index, item)} customStyles={cancelOptionStyles} text={t('common:downloadDocument')}>
                                    </MenuOption>
                                    {CreateDivider()}
                                    <MenuOption onSelect={() => removeAttachment(index, item)} customStyles={removeOptionStyles} text={t('common:remove')}>
                                    </MenuOption>
                                    {CreateDivider()}
                                    <MenuOption customStyles={cancelOptionStyles} text={t('common:cancel')}>
                                    </MenuOption>
                                </MenuOptions>
                            </Menu>
                        </View>
                    </View>
                </View>
            );
        };

        const downloadAttachment = useCallback(async (index, item) => {
            let downloadName = '';
            const fileName = item.fileName;
            const fileType = item.fileType?.toLowerCase();
            if (fileName?.toLowerCase().endsWith(`.${fileType}`)) {
                downloadName = fileName;
            } else {
                downloadName = `${fileName || item.name || 'file_' + index}.${fileType}`;
            };

            const mimeType = fileType ? `application/${fileType}` : 'application/octet-stream';

            // create blob
            const blobData = await fetch(`data:plain/text;base64,${item.fileData}`).then(res => {
                return res.blob();
            });
            // create download link
            const url = URL.createObjectURL(blobData);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                downloadName
            );

            // insert link on page
            document.body.appendChild(link);

            // start download
            link.click();

            // remove link
            link.remove();

            URL.revokeObjectURL(url);
        }, []);

        const openAttachment = async (index, item) => {
            const newWindow = window.open('', '_blank');
            if (!newWindow) {
                alert('Popup blocked. Please allow popups for this site.');
                return;
            };
            let base64Data = item.fileData;
            let type = item.fileType.toLowerCase();
            let style = '';

            if (type === 'pdf') {
                type = 'application/pdf';
                style = 'width: 100%; height: 100%; display: block;';
            } else if (['png', 'jpg', 'jpeg', 'gif'].includes(type)) {
                type = `image/${type}`;
                style = 'display: block; max-width: 100%; height: auto;';
            } else {
                // fallback MIME type
                type = 'application/octet-stream';
                style = 'display: block; max-width: 100%; height: auto;';
            }

            const uri = `data:${type};base64,${base64Data}`;

            newWindow.document.write(`
                <html>
                <head>
                    <title>${item.fileName || item.name || 'file_' + index}</title>
                    <style>
                    embed { ${style} }
                    </style>
                </head>
                <body style="margin:0">
                    <embed src="${uri}" type="${type}" />
                </body>
                </html>
            `);
            newWindow.document.close();
        };

        const pickDocument = useCallback(async (index, item) => {
            //* select attachment
            // TODO: get size from oasis settings (DB)
            const maxSize = 30 * 1048576;
            let file = null;
            let uri = null;
            let type = null;
            let ext = null;

            try {
                // TODO: add types in constants file, and get them from there
                let result = await ExpoDocumentPicker.getDocumentAsync({
                    type: [
                        "image/jpeg",
                        "image/gif",
                        "image/png",
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        "application/vnd.ms-excel",
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ],
                    copyToCacheDirectory: false,
                    multiple: false,
                });

                if (!result.canceled) {
                    file = result.output[0];
                    type = result.assets[0].mimeType;
                    uri = result.assets[0].uri.split(";base64,")[1];
                    name = file.name;
                    ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
                } else {
                    return;
                };

                // if size allowed
                if (file.size < maxSize) {
                    handleEditPress(index, item, "fileName", name);
                    handleEditPress(index, item, "fileType", ext);
                    handleEditPress(index, item, "fileData", uri);
                } else {
                    // file is too large
                    showMessage({
                        message: t('common:errFile'),
                        type: "warning",
                        duration: 3000,
                    });
                };
            } catch (error) {
                console.error(error);
            };
        }, []);


        useEffect(() => {
            attachmentsRef.current = attachments;
            if (submitClickedRef.current) {
                validateAttachments();
            };
        }, [attachments]);

        return (
            <View>
                <SafeAreaView>
                    <SafeAreaView style={styles.row}>
                        <View style={[styles.item]}>

                            <SafeAreaView style={[styles.row]}>
                                <View style={item.fileData ? styles.attachmentsExistView : styles.attachmentsNotExistView}>
                                    {item.fileName ?
                                        (showAttachments(index, item)) :
                                        <CustomLabel type='AVAILABLEUSED' value={t('common:selectAttach')} />}
                                </View>
                                <Ionicons
                                    name={"attach-outline"}
                                    style={[styles.icons, { fontSize: 30 }]}
                                    onPress={() => pickDocument(index, item)}

                                />
                            </SafeAreaView>
                        </View>
                        <View style={[styles.item]}>

                            <CustomInput
                                value={item.qualification}
                                setValue={(text) => {
                                    handleEditPress(index, item, "qualification", text);
                                }}
                                editable={true}
                                maxLen={50}
                                type="MAININPUT"
                            />
                        </View>
                        <View style={[styles.item]}>
                            <CustomInput
                                value={item.subject}
                                setValue={(text) => {
                                    handleEditPress(index, item, "subject", text);
                                }}
                                editable={true}
                                maxLen={50}
                                type="MAININPUT"
                            />
                        </View>
                        <View style={[styles.item]}>
                            <CustomDropDown
                                defValue={item.status}
                                options={additionalQualHelper.QualStatus}
                                value={item.status}
                                setValue={(e) =>
                                    handleEditPress(index, item, "status", e)
                                }
                                type="FULL"
                            />
                        </View>
                        <SafeAreaView>
                            <CustomButton
                                iconName="trash-outline"
                                onPress={() => handleDelete(item, index, 'rowGuid')}
                                show="HOME_BELL"
                                type="HOME_BELL"
                                textTop="2"
                                sz={30}
                            />
                            <Ionicons
                                name={
                                    isExpanded
                                        ? "chevron-down-outline"
                                        : "chevron-forward-outline"
                                }
                                style={styles.icons}
                                onPress={toggleExpand}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                    {isExpanded && (
                        <>
                            <SafeAreaView style={[styles.row2, { marginRight: 40 }]}>
                                <View style={[styles.item]}>
                                    <SafeAreaView style={[styles.row]}>
                                        <SafeAreaView>
                                            <CustomLabel value={t("common:doa")} type="SUBTITLE" />
                                            <CustomInput
                                                value={formatDateForUI(item.date)}
                                                setValue={() => { }}
                                                maxLen={50}
                                                editable={false}
                                                type="MAININPUT"
                                            />
                                        </SafeAreaView>
                                        <Ionicons
                                            name={showCalendarDoa ? "calendar" : "calendar-outline"}
                                            style={styles.icons}
                                            onPress={() => openCalendar('date', item.date)}
                                        />
                                    </SafeAreaView>
                                </View>
                                <View style={[styles.item]}>
                                    <CustomLabel value={t("common:grade")} type="SUBTITLE" />
                                    <CustomInput
                                        value={item.grade}
                                        setValue={(text) => {
                                            handleEditPress(index, item, "grade", text);
                                        }}
                                        editable={true}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </View>
                                <View style={[styles.item]}>
                                    <CustomLabel value={t("common:level")} type="SUBTITLE" />
                                    <CustomInput
                                        value={item.level}
                                        setValue={(text) => {
                                            handleEditPress(index, item, "level", text);
                                        }}
                                        editable={true}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </View>
                            </SafeAreaView>
                        </>
                    )}
                </SafeAreaView>
            </View>
        );
    }
);

const HRAdminAdditionalQualifications = (props) => {
    const { width } = useWindowDimensions();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width);
    const [changes, setChanges] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [additionalQualHelper, setAdditionalQualHelper] = useState({});
    const [additionalQual, setAdditionalQual] = useState([]);
    const { t } = useTranslation();
    const ref = useRef(null);
    const { GetAdditionalQualHelper, GetAdditionalQual, SaveAdditionalQual, deleteAdditionalQual, isLoading } = useHRAdminEmployees();
    const { rowAdd, rowEdit, rowDelete } = useTableHelper();
    const [resetDates, setResetDates] = useState([false]);
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [calendarFieldName, setCalendarFieldName] = useState('');
    const [calendarItemIndex, setCalendarItemIndex] = useState(null);
    const [calendarSelectedData, setCalendarSelectedData] = useState({ startDate: '', endDate: '' });

    const showWarningPopup = async () => {
        return Swal.fire({
            title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:changeSchemeMessage"
            )}</text>`,
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:cancel"
            )}</text>`,
            cancelButtonColor: "#d3d3d3",
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:confirmMessage"
            )}</text>`,
            confirmButtonColor: "#A0A2FF",
        });
    };

    useEffect(() => {
        if (props.selectedEmployee?.employeeID) {
            loadData();
        }
    }, [props.selectedEmployee]);

    const loadData = () => {
        GetAdditionalQualHelper()
            .then((helper) => {
                setAdditionalQualHelper(helper);
                return GetAdditionalQual(props.selectedEmployee.employeeID);
            })
            .then((qualifications) => {
                setAdditionalQual(qualifications);
                setOriginalData(qualifications);
            })
            .catch((error) => {
                console.error('Error in loadData:', error);
            });
    };

    const getValidDateOrToday = (dateStr) => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        const dateOnly = dateStr.split('T')[0];
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (isoDateRegex.test(dateOnly)) {
            return dateOnly;
        }
        return new Date().toISOString().split('T')[0];
    };

    const openCalendarForItem = (index, fieldName, currentDate) => {
        const validDate = getValidDateOrToday(currentDate);
        setCalendarItemIndex(index);
        setCalendarFieldName(fieldName);
        setCalendarSelectedData({ startDate: validDate, endDate: validDate });
        setCalendarVisible(true);
    };

    const closeCalendar = () => {
        setCalendarVisible(false);
        setCalendarItemIndex(null);
        setCalendarFieldName('');
    };

    const onCalendarDateChange = (newDates) => {
        setCalendarSelectedData(newDates);
        if (calendarItemIndex !== null) {
            handleEditPress(calendarItemIndex, additionalQual[calendarItemIndex], calendarFieldName, newDates.startDate);
        }
        closeCalendar();
    };

    const handleEditPress = useCallback(
        async (index, item, key, value) => {
            // Check if the value has changed
            if (item[key] === value) {
                return;
            }
            // Store the original value for potential rollback
            // Use rowEdit to apply the change
            rowEdit(
                index,
                item,
                key,
                value,
                changes,
                setChanges,
                additionalQual,
                setAdditionalQual
            );
        },
        [showWarningPopup, rowEdit, changes, additionalQual, setAdditionalQual]
    );

    const handleCancelChanges = () => {
        const rolledBackReviews = originalData.map((obj) => ({ ...obj }));
        setAdditionalQual(rolledBackReviews);
        setDeletedItems([]);
        setChanges({});
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
        setResetDates(true);
    };

    // * Validator 
    // TODO Can be abstracted as well probably -- But column names matter ? 
    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')]
        additionalQual.forEach((itr) => {
            // * Check empty description
            if (itr.status === '')
                ret = [false, t('common:statEmpty')]
            else if (itr.subject === '')
                ret = [false, t('common:subEmpty')]
            else if (itr.qualification === '')
                ret = [false, t('common:QualEmpty')]
        })
        return (ret)
    }

    const handleSave = async () => {
        try {
            if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
                showMessage({
                    message: t('common:noChanges'),
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let valRet = validateInput();
            if (!valRet[0]) {
                showMessage({
                    message: valRet[1],
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let savePromises = [];
            let updateSucc = true;
            let insertSucc = true;

            if (Object.entries(changes).length > 0) {
                savePromises.push(
                    SaveAdditionalQual(
                        changes,
                        props?.selectedEmployee?.employeeID
                    ).then((resp) => {
                        updateSucc = resp;
                    }).catch((error) => {
                        console.error("Save error", error);
                        updateSucc = false;
                    })
                );
            }

            if (deletedItems.length > 0) {
                savePromises.push(deleteAdditionalQual(deletedItems)
                    .then((resp) => {
                        deleteSucc = resp
                        removeFromChangesAfterSave();
                    }).catch(() => {
                        console.error('delete failed', error);
                        deleteSucc = false;
                    }))
            }
            // Wait for all promises to resolve
            await Promise.all(savePromises);
            // === Handle results ===
            if (!updateSucc || !insertSucc) {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
            // * Reload after save
            loadData();
            removeFromChangesAfterSave();
            setResetDates(true);
            // === Success message ===
            showMessage({
                message: t("common:academicQualSuccessSave"),
                type: "success",
                duration: 3000,
            });

        } catch (error) {
            showMessage({
                message: t("common:errorSave"),
                type: "danger",
                duration: 3000,
            });
        }
    };

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, additionalQual, setAdditionalQual, setDeletedItems, ref)
    }

    const removeFromChangesAfterSave = () => {
        setDeletedItems([]);
        setChanges({});
    };

    const renderAdditionalQualifications = useCallback(
        ({ item, index }) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <AdditionalQualificationsListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        additionalQualHelper={additionalQualHelper}
                        styles={styles}
                        t={t}
                        handleDelete={handleDelete}
                        resetDates={resetDates}
                        openCalendar={(fieldName, currentDate) => openCalendarForItem(index, fieldName, currentDate)}
                    />
                </Card.Content>
            </Card>
        ),
        [
            additionalQual,
            handleEditPress,
            handleDelete,
            styles,
            resetDates,
        ]
    );

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={styles.container}>
                <View style={[styles.buttons, styles.functionalityButtons]}>
                    <CustomButton
                        text={t("common:new")}
                        onPress={() => {
                            rowAdd(
                                additionalQual,
                                setAdditionalQual,
                                ['rowGuid', 'employeeId'],
                                [],
                                ref,
                                t,
                                ['employeeId'],
                                ['type'],
                                changes,
                                setChanges,
                                undefined,
                                undefined,
                                "rowGuid",
                                false,
                                ['date'],
                            );
                        }}
                        type="BUTTON_ADD"
                        textTop="0"
                        sz={15}
                    />
                    {(Object.keys(changes).length > 0 ||
                        changes.length > 0 || deletedItems.length > 0) && (
                            <CustomButton
                                text={t("common:saveFunction")}
                                onPress={handleSave}
                                type="BUTTON_SAVE"
                                isGradient={true}
                                textTop="0"
                                sz={15}
                            />
                        )}
                    <CustomButton
                        text={t("common:cancel")}
                        onPress={handleCancelChanges}
                        type="BUTTON_CANCEL"
                        textTop="0"
                        sz={15}
                    />
                </View>
                <FlatList
                    data={additionalQual}
                    keyExtractor={(item, index) => `${item.rowGuid}-${index}`}
                    renderItem={renderAdditionalQualifications} // Use the memoized function here
                    refreshing={isLoading}
                    ref={ref}
                    ListHeaderComponent={
                        <SafeAreaView style={common.listTitle}>
                            <SafeAreaView style={styles.row}>
                                <CustomLabel value={t("common:doc")} type="SUBTITLE" />
                                <CustomLabel value={t("common:Qualification")} type="SUBTITLE" />
                                <CustomLabel value={t("common:subject")} type="SUBTITLE" />
                                <CustomLabel value={t("common:Status")} type="SUBTITLE" />
                                <SafeAreaView style={[{ marginRight: '5%' }]} />
                            </SafeAreaView>
                            <SafeAreaView style={common.divider} />
                        </SafeAreaView>
                    }
                    ListEmptyComponent={
                        isLoading ? (
                            <ActivityIndicator animating={true} color={colors.blue} />
                        ) : (
                            <CustomLabel
                                value={t("common:noReviewDataPreview")}
                                type="NO_FUNCTIONS"
                            />
                        )
                    }
                    initialNumToRender={4}
                />
                {calendarVisible && (
                    <>
                        <View
                            style={styles.overlay}
                            onStartShouldSetResponder={() => true}
                            onResponderRelease={closeCalendar}
                        />
                        <SafeAreaView style={styles.calendarOverlay}>
                            <CustomCalendar
                                calendarType={"RangePicker"}
                                setRange={onCalendarDateChange}
                                range={calendarSelectedData}
                                allowSingleDate={true}
                                startingYear={1950}
                            />
                        </SafeAreaView>
                    </>
                )}
            </View>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) =>
    StyleSheet.create({
        mainView: {
            flex: 1,
        },
        rowView: {
            flex: 1,
            flexDirection: "row",
            justifyContent: "center",
        },
        container: {
            flex: 1,
        },
        cardStyleBranch: {
            margin: 5,
            marginHorizontal: 10,
            borderRadius: 5,
            elevation: 5,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 5,
            backgroundColor: colors.background_card,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        row2: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
            margin: 10
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6
        },
        itemCurrency: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
            flexDirection: "column",
            gap: 10
        },
        buttons: {
            flexDirection: "row",
            justifyContent: "flex-end",
            alignSelf: "flex-end",
            marginRight: 15,
        },
        functionalityButtons: {
            justifyContent: "flex-end",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
        icons: {
            fontSize: 24,
            marginHorizontal: 10,
        },
        backButton: {
            flexDirection: "row",
            justifyContent: "flex-end",
        },
        checkboxContainer: {
            flexDirection: 'row',
        },
        attachmentContainer: {
            flexDirection: 'row',
            borderRadius: 10,
            marginHorizontal: '3%',
            marginVertical: '1%',
            justifyContent: 'center',
            alignItems: 'center',
        },
        attachmentsNotExistView: {
            flex: 1,
            borderColor: colors.border_card,
            borderWidth: 2,
            elevation: 5,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 5,
            shadowColor: colors.card_line,
            backgroundColor: colors.background_card,
            borderRadius: 10,
            padding: 5,
            margin: 10,
        },
        attachmentIcons: {
            width: 20,
            alignSelf: 'center',
            color: colors.text_primary,
        },
        attachmentNameContainer: {
            flex: 7,
            alignItems: 'flex-start',
            paddingHorizontal: 12,
        },
        attachmentNameText: {
            fontSize: 16,
            color: colors.text_primary,
            alignSelf: 'center',
        },
        dotsIcon: {
            fontSize: 21,
            color: colors.text_primary
        },
        customLabelIconContainer: {
            marginLeft: '3%',
            width: '94%',
            flexDirection: 'row',
            marginTop: '4%',
        },
        customLabelContainer: {
            width: '90%',
            justifyContent: 'center',
            alignItems: 'flex-start',
        },
        attachmentsExistView: {
            flex: 1,
            borderColor: colors.border_card,
            borderWidth: 2,
            elevation: 5,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 5,
            shadowColor: colors.card_line,
            backgroundColor: colors.background_card,
            borderRadius: 10,
            padding: 5,
            margin: 10,
        },
        calendarOverlay: {
            position: 'absolute',
            top: '10%',
            left: '50%',
            transform: [{ translateX: -160 }],
            backgroundColor: 'white',
            borderRadius: 10,
            zIndex: 999,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 6 },
            shadowOpacity: 0.5,
            shadowRadius: 20,
            elevation: 15,
        },
        overlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
        },
    });
const optionsStyles = {
    optionsContainer: {
        marginHorizontal: '3.5%',
        marginVertical: '-3%',
        borderRadius: 10,
    },
    optionWrapper: {
        height: 50,
        justifyContent: 'center',
    },
};
const removeOptionStyles = {
    optionText: {
        textAlign: 'center',
        fontSize: 16,
        color: '#dd3636',
    },
};
const cancelOptionStyles = {
    optionText: {
        textAlign: 'center',
        fontSize: 16,
    },
};

export default HRAdminAdditionalQualifications;
